import {
  ApolloError,
  TypedDocumentNode,
  WatchQueryFetchPolicy,
  useQuery,
} from "@apollo/client";
import FilterList from "@mui/icons-material/FilterList";
import {
  Box,
  Stack,
  SxProps,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { DataGridProps } from "components/DataGrid";
import { Omnisearch } from "components/Omnisearch/Omnisearch";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  FilterKey,
  OmnisearchGridColDef,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { CommandButton } from "./CommandButton";
import {
  FilterGroup,
  InputValues,
  parseOmnisearch,
  stringifyOmnisearch,
} from "./Omnisearch/FilterPopover";
import { TierIIYearPicker } from "./TierIIYearPicker";

type OmnisearchInputRef = React.RefObject<HTMLInputElement | null>;
type OmnisearchResetButtonRef = React.RefObject<HTMLButtonElement | null>;
export type OmnisearchCommandButtonsContext = {
  search: string;
};

export type DataDisplayProps<TData, TRow extends GridValidRowModel> = {
  loading: boolean;
  previousData: TData | undefined;
  data: TData | undefined;
  error: ApolloError | undefined;
  items: TRow[];
  count: number;
  paginationModel: GridPaginationModel;
  setPaginationModel: (model: GridPaginationModel) => void;
};

export type OmnisearchDataDisplayProps<
  TData,
  TVariables,
  TRow extends GridValidRowModel
> = {
  /** Component to render the data. */
  dataDisplay: (props: DataDisplayProps<TData, TRow>) => React.ReactNode;
  omnisearchInputRef?: OmnisearchInputRef;
  omnisearchResetButtonRef?: OmnisearchResetButtonRef;
  autoFocus?: boolean;
  /** key to be used as an identifier for persisted state in localStorage */
  persistenceKey: string;
  columns: OmnisearchGridColDef<TRow>[];
  dataQuery: TypedDocumentNode<TData, TVariables>;
  dataQueryVariables?: TVariables;
  defaultSearch?: string;
  manageColumnsCommandButton?: React.ReactNode;
  /**
   * Command buttons to display to the right of the omnisearch input.
   */
  commandButtons?:
    | React.ReactNode[]
    | ((context: OmnisearchCommandButtonsContext) => React.ReactNode[]);
  /**
   * Command buttons to display to the left of the omnisearch input.
   */
  startCommandButtons?:
    | React.ReactNode[]
    | ((context: OmnisearchCommandButtonsContext) => React.ReactNode[]);
  /** sx for the Box wrapping the component */
  sx?: SxProps<Theme>;

  omnisearchWrapperSx?: SxProps<Theme>;
  /** Additional columns to exclude from the Omnisearch filter popover. ("actions" is excluded by default) */
  excludeFilterColumns?: GridColDef["field"][];
  additionalFilterColumns?: FilterKey[];
  initialFilters?: { field: string; value: string | boolean }[];

  /** onClick handler for the button displayed in the grid when there is no data for the default search */
  noDataOnButtonClick?: () => void;
  getItems: (data: TData) => TRow[];
  getCount: (data: TData) => number;
  initialPageSize?: number;

  /**
   * Function to handle the datagrid's onLoadingStateChange event.
   */
  onLoadingStateChange?: (loading: boolean) => void;
  onSearchChanged?: (search: string) => void;
  /**
   * Apollo client fetch policy. Defaults to "cache-and-network"
   */
  fetchPolicy?: WatchQueryFetchPolicy;
  /**
   * Indicates if Omnisearch should use the url querystring. Set to false to use
   * an internal search variable (i.e. for a modal).
   */
  isURLDriven?: boolean;
  otherLoading?: boolean;
  showFilters?: boolean;
  hideOmnisearch?: boolean;
  /** when true, provide extra padding around grid */
  withPadding?: boolean;
  /** (optional) skip running query if true. Defaults to false */
  skip?: boolean;
  shouldPoll?: (data: TData) => number | undefined;
  onQueryError?: (error: ApolloError) => void;
  onApolloLoadingStateChange?: (loading: boolean) => void;
  onPreviousDataStateChange?: (previousData: TData | undefined) => void;
  onDataStateChange?: (data: TData | undefined) => void;
  onQueryStateChange?: (state: {
    data: TData | undefined;
    loading: boolean;
  }) => void;
  getRowLink?: DataGridProps<TRow>["getRowLink"];
  /** Optional filter groups for organizing filters in the popover */
  filterGroups?: FilterGroup[];
  /** Optional key aliases for omnisearch (e.g., { tags: "facilityTags" }) */
  keyAliases?: Record<string, string>;
  /**
   * Optional tenantId for backend queries. Will default to tenantId from the URL if present. (e.g. for customer routes)
   * Staff routes will not have the tenantId in the URL
   * - Do not provide tenantId if you intentionally want unscoped filters, such as on the fulfillment grid
   * - Provide the tenantId when you are operating with records specific to a tenant, such as with payments or mail.
   */
  tenantId?: string;
  /** Optional key for the year picker. */
  yearPickerKey?: string;
  /** Optional default value for the year picker. */
  yearPickerDefaultValue?: string;
  /** Optional boolean to show the "All" option in the year picker. Selecting All will remove the year filter. */
  yearPickerShowAllOption?: boolean;
  /** Optional handler for when the year picker value changes. */
  onYearPickerChange?: (year: string | undefined) => void;
};

/** This component displays omnisearch bar and controls, fetches data,
 *  and passes the data to the `dataDisplay` component provided in the props. */
export function OmnisearchDataDisplay<
  TData,
  TVariables,
  TRow extends GridValidRowModel
>({
  omnisearchInputRef,
  omnisearchResetButtonRef,
  autoFocus,
  persistenceKey,
  columns: columnsInput,
  dataQuery,
  dataQueryVariables,
  defaultSearch = "",
  commandButtons: commandButtons,
  startCommandButtons,
  manageColumnsCommandButton,
  sx,
  omnisearchWrapperSx,
  excludeFilterColumns,
  additionalFilterColumns,
  initialFilters,
  getItems,
  getCount,
  onLoadingStateChange,
  onSearchChanged,
  fetchPolicy = "cache-and-network",
  initialPageSize,
  isURLDriven = false,
  otherLoading = false,
  showFilters = true,
  hideOmnisearch = false,
  withPadding = true,
  skip = false,
  shouldPoll,
  onQueryError,
  onApolloLoadingStateChange,
  onPreviousDataStateChange,
  onDataStateChange,
  onQueryStateChange,
  filterGroups,
  keyAliases,
  tenantId,
  yearPickerKey,
  yearPickerDefaultValue,
  yearPickerShowAllOption,
  onYearPickerChange,
  dataDisplay,
}: OmnisearchDataDisplayProps<TData, TVariables, TRow>) {
  const theme = useTheme();
  const { user } = useCurrentUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { tenantId: tenantIdFromParams } = useParams<{ tenantId: string }>();
  const selectedTenantId = tenantId ?? tenantIdFromParams;

  const {
    omnisearch,
    debouncedOmnisearch,
    paginationModel,
    sortModel,
    setOmnisearch,
    setPaginationModel,
    columnFilterKeys,
  } = useOmnisearchDatagrid({
    persistenceKey,
    columns: columnsInput,
    additionalFilters: additionalFilterColumns,
    excludeFilters: excludeFilterColumns,
    isURLDriven,
    initialPageSize,
    initialFilters,
    isMobile,
  });

  // Track previous page size to detect changes without causing re-renders
  const previousPageSizeRef = useRef(paginationModel.pageSize);

  // Reset to page 0 when page size changes
  useEffect(() => {
    if (paginationModel.pageSize !== previousPageSizeRef.current) {
      previousPageSizeRef.current = paginationModel.pageSize;
      // Only reset if we're not already on page 0
      if (paginationModel.page > 0) {
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      }
    }
  }, [paginationModel.pageSize, paginationModel.page, setPaginationModel]);

  const getSearchString = useCallback(
    (defaultSearch: string, omnisearch: string) => {
      return [defaultSearch, omnisearch].join(" ").trim();
    },
    []
  );

  const [search, setSearch] = useState(() => {
    return getSearchString(defaultSearch, omnisearch);
  });
  // Use this for any readers who don't want to block rerender
  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    setSearch(getSearchString(defaultSearch, debouncedOmnisearch));
  }, [defaultSearch, debouncedOmnisearch, getSearchString]);

  useEffect(
    () => onSearchChanged && onSearchChanged(search),
    [search, onSearchChanged]
  );

  const {
    data,
    error,
    loading: dataLoading,
    previousData,
    startPolling,
    stopPolling,
  } = useQuery(dataQuery, {
    variables: {
      search,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sort: sortModel,
      ...dataQueryVariables,
    },
    fetchPolicy,
    skip,
    onError: onQueryError,
  });

  useEffect(() => {
    if (onApolloLoadingStateChange) onApolloLoadingStateChange(dataLoading);
  }, [dataLoading, onApolloLoadingStateChange]);

  useEffect(() => {
    if (onPreviousDataStateChange) onPreviousDataStateChange(previousData);
  }, [previousData, onPreviousDataStateChange]);

  useEffect(() => {
    if (onDataStateChange) onDataStateChange(data);
  }, [data, onDataStateChange]);

  useEffect(() => {
    if (onQueryStateChange) onQueryStateChange({ data, loading: dataLoading });
  }, [data, dataLoading, onQueryStateChange]);

  useEffect(() => {
    const pollInterval = data ? shouldPoll?.(data) : undefined;
    if (pollInterval) {
      startPolling(pollInterval);
    } else {
      stopPolling();
    }
  }, [startPolling, stopPolling, shouldPoll, data]);

  const [items, count] = useMemo(() => {
    if (error) return [[], 0];
    if (data) return [getItems(data), getCount(data)];
    if (dataLoading && previousData)
      return [getItems(previousData), getCount(previousData)];
    return [[], 0];
  }, [data, previousData, dataLoading, error, getItems, getCount]);

  const loading =
    dataLoading ||
    otherLoading ||
    search !== getSearchString(defaultSearch, omnisearch);

  useEffect(() => {
    if (onLoadingStateChange) onLoadingStateChange(loading);
  }, [loading, onLoadingStateChange]);

  const onApplyFilters = useCallback(
    (newOmnisearch: string) => {
      setOmnisearch(newOmnisearch);
      // We want to update the search immediately when new filters are applied
      setSearch(getSearchString(defaultSearch, newOmnisearch));
    },
    [defaultSearch, setOmnisearch, getSearchString]
  );

  const [filterButtonAnchor, setFilterButtonAnchor] =
    useState<HTMLButtonElement | null>(null);

  const handleFilterButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setFilterButtonAnchor(event.currentTarget);
    },
    []
  );

  const handleCloseFilter = useCallback(() => {
    setFilterButtonAnchor(null);
  }, []);

  const yearPickerValue = useMemo<string | undefined>(() => {
    if (yearPickerKey) {
      const filters = parseOmnisearch(omnisearch, keyAliases);
      return (
        (filters[yearPickerKey] as string | undefined) ?? yearPickerDefaultValue
      );
    }
    return undefined;
  }, [yearPickerKey, yearPickerDefaultValue, omnisearch, keyAliases]);

  const onYearPickerValueChanged = useCallback(
    (value: string | undefined) => {
      if (yearPickerKey) {
        const prev = parseOmnisearch(omnisearch, keyAliases);
        const newFilters = {
          ...prev,
          [yearPickerKey as keyof typeof prev]: value,
        } as InputValues;
        setOmnisearch(stringifyOmnisearch(newFilters, keyAliases));
      }
      if (onYearPickerChange) onYearPickerChange(value);
    },
    [yearPickerKey, omnisearch, keyAliases, setOmnisearch, onYearPickerChange]
  );

  const renderedCommandButtons = useMemo(() => {
    if (!commandButtons) return [];

    if (Array.isArray(commandButtons)) {
      return commandButtons;
    }

    if (typeof commandButtons === "function") {
      return commandButtons({
        search: deferredSearch,
      });
    }

    return [];
  }, [commandButtons, deferredSearch]);

  const renderedStartCommandButtons = useMemo(() => {
    if (!startCommandButtons) return [];

    if (Array.isArray(startCommandButtons)) {
      return startCommandButtons;
    }

    if (typeof startCommandButtons === "function") {
      return startCommandButtons({
        search: deferredSearch,
      });
    }

    return [];
  }, [startCommandButtons, deferredSearch]);

  if (!user) return <></>;
  return (
    <Box sx={{ px: withPadding ? 3 : 0 }}>
      <Stack
        sx={{
          marginBottom: !hideOmnisearch ? theme.spacing(3) : theme.spacing(2),
          gap: theme.spacing(1.5),
          ...omnisearchWrapperSx,
        }}
        direction="row"
        flexWrap="wrap"
        alignItems="center"
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            width: {
              xs: "100%",
              lg: "inherit",
            },
            maxWidth: "1022px",
            flexGrow: 1,
          }}
          gap={theme.spacing(1.5)}
        >
          {renderedStartCommandButtons.length > 0 &&
            renderedStartCommandButtons}
          {!hideOmnisearch && (
            <Omnisearch
              inputRef={omnisearchInputRef}
              resetRef={omnisearchResetButtonRef}
              autoFocus={autoFocus}
              omnisearch={omnisearch}
              setOmnisearch={setOmnisearch}
              onApplyFilters={onApplyFilters}
              columnFilterKeys={columnFilterKeys}
              filterGroups={filterGroups}
              showFilterButtonInside={false}
              filterButtonAnchor={filterButtonAnchor}
              onFilterButtonClick={handleFilterButtonClick}
              onFilterClose={handleCloseFilter}
              persistenceKey={persistenceKey}
              keyAliases={keyAliases}
              tenantId={selectedTenantId}
              sx={{ flexGrow: 1 }}
            />
          )}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            gap: theme.spacing(1.5),
            flexWrap: "wrap",
          }}
        >
          {showFilters && columnFilterKeys && columnFilterKeys.length > 0 && (
            <CommandButton onClick={handleFilterButtonClick}>
              <FilterList sx={{ mr: 1 }} fontSize="small" />
              Filters
            </CommandButton>
          )}

          {yearPickerKey && (
            <TierIIYearPicker
              persistQueryParam={false}
              showAllOption={yearPickerShowAllOption}
              value={yearPickerValue}
              onChange={(value) =>
                onYearPickerValueChanged(value ? String(value) : undefined)
              }
            />
          )}

          {manageColumnsCommandButton && manageColumnsCommandButton}
          {renderedCommandButtons.length > 0 && renderedCommandButtons}
        </Stack>
      </Stack>
      <Box position="relative" sx={sx}>
        {dataDisplay({
          data,
          items,
          count,
          loading,
          previousData,
          error,
          paginationModel,
          setPaginationModel,
        })}
      </Box>
    </Box>
  );
}
